const DEFAULT_LOCALE = new Intl.Locale("en-US");

const resolveLocale = (locale?: string | Intl.Locale) => {
  if (!locale) {
    return DEFAULT_LOCALE.baseName;
  }

  if (typeof locale === "string") {
    return locale;
  }

  return locale.baseName;
};

type FormatPercentOptions = {
  style?: "adaptive" | "fixed";
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
};

export const formatCompactNumber = (
  value: number,
  locale?: string | Intl.Locale,
  maximumFractionDigits = 1,
) => {
  try {
    return new Intl.NumberFormat(resolveLocale(locale), {
      notation: "compact",
      maximumFractionDigits,
    }).format(value);
  } catch {
    if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B`;
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
    return String(value);
  }
};

export const formatInteger = (value: number, locale?: string | Intl.Locale) =>
  new Intl.NumberFormat(resolveLocale(locale), {
    maximumFractionDigits: 0,
  }).format(value);

export const formatDecimal = (
  value: number,
  locale?: string | Intl.Locale,
  options?: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
    maximumSignificantDigits?: number;
  },
) =>
  new Intl.NumberFormat(resolveLocale(locale), {
    minimumFractionDigits: options?.minimumFractionDigits,
    maximumFractionDigits: options?.maximumFractionDigits,
    maximumSignificantDigits: options?.maximumSignificantDigits,
  }).format(value);

export const formatSignedDecimal = (
  value: number,
  locale?: string | Intl.Locale,
  options?: {
    maximumFractionDigits?: number;
  },
) =>
  new Intl.NumberFormat(resolveLocale(locale), {
    signDisplay: "always",
    maximumFractionDigits: options?.maximumFractionDigits ?? 1,
  }).format(value);

export const formatPercentFromWhole = (
  value: number,
  locale?: string | Intl.Locale,
  optionsOrMaximumFractionDigits?: number | FormatPercentOptions,
  minimumFractionDigits = 0,
) => {
  const options: FormatPercentOptions =
    typeof optionsOrMaximumFractionDigits === "number"
      ? {
          style: "fixed",
          maximumFractionDigits: optionsOrMaximumFractionDigits,
          minimumFractionDigits,
        }
      : (optionsOrMaximumFractionDigits ?? { style: "adaptive" });

  const style = options.style ?? "adaptive";
  const absValue = Math.abs(value);
  const formatterOptions =
    style === "adaptive"
      ? absValue === 0 || absValue >= 1
        ? {
            style: "percent" as const,
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
          }
        : {
            style: "percent" as const,
            maximumFractionDigits: 1,
            minimumFractionDigits: 1,
          }
      : {
          style: "percent" as const,
          maximumFractionDigits: options.maximumFractionDigits ?? 0,
          minimumFractionDigits: options.minimumFractionDigits ?? 0,
        };

  return new Intl.NumberFormat(resolveLocale(locale), formatterOptions).format(value / 100);
};
